import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError, formatINR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Spinner, PrimaryButton, GhostButton, Card, Input, Textarea } from "@/components/common";
import { StatusBadge } from "@/components/StatusBadge";
import { ArrowLeft, Plus, Trash2, FileCheck, Check, X } from "lucide-react";
import { toast } from "sonner";

const newItem = () => ({ _k: Math.random().toString(36).slice(2), description: "", quantity: 1, unit_price: 0 });


export default function EnquiryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [data, setData] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [items, setItems] = useState(() => [newItem()]);
  const [tax, setTax] = useState(18);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const isOps = hasRole("ops", "admin");
  const load = () => api.get(`/enquiries/${id}`).then((r) => setData(r.data)).catch((e) => { toast.error(apiError(e)); navigate("/app/enquiries"); });
  useEffect(() => { load(); }, [id]);
  if (!data) return <Spinner />;
  const { enquiry, quotations } = data;

  const subtotal = items.reduce((s, i) => s + Number(i.quantity || 0) * Number(i.unit_price || 0), 0);
  const taxAmt = subtotal * Number(tax || 0) / 100;
  const total = subtotal + taxAmt;

  const setItem = (idx, k, v) => setItems((arr) => arr.map((it, i) => i === idx ? { ...it, [k]: v } : it));
  const addItem = () => setItems((a) => [...a, newItem()]);
  const removeItem = (idx) => setItems((a) => a.filter((_, i) => i !== idx));

  const submitQuote = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.post(`/enquiries/${id}/quotations`, {
        line_items: items.map((i) => ({ description: i.description, quantity: Number(i.quantity), unit_price: Number(i.unit_price) })),
        tax_percent: Number(tax), notes,
      });
      toast.success("Quotation sent"); setQuoting(false); setItems([newItem()]); setNotes(""); load();
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };

  const approve = async (qid) => {
    try { const r = await api.post(`/quotations/${qid}/approve`); toast.success("Approved - booking created"); if (r.data?.id) navigate(`/app/bookings/${r.data.id}`); else load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const reject = async (qid) => {
    try { await api.post(`/quotations/${qid}/reject`); toast.success("Quotation rejected"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="animate-fadeUp">
      <button onClick={() => navigate("/app/enquiries")} className="mb-4 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back to enquiries
      </button>
      <PageHeader title={enquiry.title} subtitle={`${enquiry.reference} · ${enquiry.institution_name}`}
        action={<StatusBadge status={enquiry.status} />} />

      <div className="grid gap-4 lg:grid-cols-3">
        <Card>
          <h3 className="mb-3 font-display text-lg font-semibold">Details</h3>
          <Detail label="Type" value={enquiry.type} />
          <Detail label="Sport" value={enquiry.sport} />
          <Detail label="Headcount" value={enquiry.headcount} />
          <Detail label="Dates" value={`${enquiry.preferred_start || "-"} → ${enquiry.preferred_end || "-"}`} />
          {enquiry.destination && <Detail label="Destination" value={enquiry.destination} />}
          {enquiry.notes && <div className="mt-3"><p className="font-mono text-xs uppercase text-muted-foreground">Notes</p><p className="text-sm">{enquiry.notes}</p></div>}
        </Card>

        <Card className="lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-display text-lg font-semibold">Quotations</h3>
            {isOps && <PrimaryButton data-testid="create-quotation-btn" onClick={() => setQuoting((q) => !q)}><Plus className="h-4 w-4" /> New quotation</PrimaryButton>}
          </div>

          {quotations.length === 0 && !quoting && <p className="py-6 text-center text-sm text-muted-foreground">No quotations yet.</p>}

          <div className="space-y-3">
            {quotations.map((q) => (
              <div key={q.id} data-testid={`quotation-${q.id}`} className="rounded-xl border border-border bg-background/40 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-mono text-xs text-primary">{q.reference}</span>
                    <span className="ml-2 text-xs text-muted-foreground">v{q.version}</span>
                  </div>
                  <StatusBadge status={q.status} />
                </div>
                <div className="mt-3 space-y-1 text-sm">
                  {q.line_items.map((li, i) => (
                    <div key={i} className="flex justify-between text-muted-foreground">
                      <span>{li.description} <span className="font-mono text-xs">×{li.quantity}</span></span>
                      <span className="font-mono">{formatINR(li.quantity * li.unit_price)}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-2 border-t border-border pt-2 text-sm">
                  <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span className="font-mono">{formatINR(q.subtotal)}</span></div>
                  <div className="flex justify-between text-muted-foreground"><span>Tax ({q.tax_percent}%)</span><span className="font-mono">{formatINR(q.tax)}</span></div>
                  <div className="flex justify-between font-semibold"><span>Total</span><span className="font-mono text-primary">{formatINR(q.total)}</span></div>
                </div>
                {q.notes && <p className="mt-2 text-xs text-muted-foreground">{q.notes}</p>}
                {q.status === "sent" && hasRole("school_coordinator", "ops", "admin") && (
                  <div className="mt-3 flex gap-2">
                    <PrimaryButton data-testid={`approve-quote-${q.id}`} onClick={() => approve(q.id)} className="py-2"><Check className="h-4 w-4" /> Approve</PrimaryButton>
                    <GhostButton data-testid={`reject-quote-${q.id}`} onClick={() => reject(q.id)} className="py-2"><X className="h-4 w-4" /> Reject</GhostButton>
                  </div>
                )}
                {q.status === "approved" && <p className="mt-3 flex items-center gap-2 text-sm text-emerald-400"><FileCheck className="h-4 w-4" /> Approved - booking created</p>}
              </div>
            ))}
          </div>

          {quoting && (
            <form onSubmit={submitQuote} className="mt-4 rounded-xl border border-primary/30 bg-primary/5 p-4">
              <p className="mb-3 font-display text-lg font-semibold">Build quotation</p>
              <div className="space-y-2">
                {items.map((it, idx) => (
                  <div key={it._k} className="grid grid-cols-12 gap-2">
                    <input data-testid={`quote-desc-${idx}`} placeholder="Description" value={it.description} onChange={(e) => setItem(idx, "description", e.target.value)} required
                      className="col-span-6 rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
                    <input data-testid={`quote-qty-${idx}`} type="number" min={0} placeholder="Qty" value={it.quantity} onChange={(e) => setItem(idx, "quantity", e.target.value)}
                      className="col-span-2 rounded-lg border border-input bg-background px-2 py-2 text-sm outline-none focus:border-primary" />
                    <input data-testid={`quote-price-${idx}`} type="number" min={0} placeholder="Unit ₹" value={it.unit_price} onChange={(e) => setItem(idx, "unit_price", e.target.value)}
                      className="col-span-3 rounded-lg border border-input bg-background px-2 py-2 text-sm outline-none focus:border-primary" />
                    <button type="button" onClick={() => removeItem(idx)} className="col-span-1 rounded-lg text-muted-foreground hover:text-destructive"><Trash2 className="mx-auto h-4 w-4" /></button>
                  </div>
                ))}
              </div>
              <button type="button" onClick={addItem} className="mt-2 text-sm text-primary hover:underline">+ Add line item</button>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <Input label="Tax %" testId="quote-tax" type="number" value={tax} onChange={(e) => setTax(e.target.value)} />
                <div className="flex items-end justify-end">
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Total</p>
                    <p data-testid="quote-total-preview" className="font-display text-2xl font-bold text-primary">{formatINR(total)}</p>
                  </div>
                </div>
              </div>
              <Textarea label="Notes" testId="quote-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-3" />
              <div className="mt-3 flex justify-end gap-2">
                <GhostButton type="button" onClick={() => setQuoting(false)}>Cancel</GhostButton>
                <PrimaryButton data-testid="send-quotation-btn" type="submit" disabled={saving}>Send quotation</PrimaryButton>
              </div>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
}

const Detail = ({ label, value }) => (
  <div className="flex justify-between gap-4 border-b border-border/50 py-1.5 text-sm last:border-0">
    <span className="text-muted-foreground">{label}</span><span className="font-medium capitalize">{String(value ?? "-")}</span>
  </div>
);
